import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Waves, Search, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const WavePickingPage = () => {
  const [waves, setWaves] = useState([]);
  const [filteredWaves, setFilteredWaves] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchWaves = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/wave-picking/waves`);
      const data = await response.json();
      setWaves(data);
    } catch (error) {
      toast.error('Failed to load waves');
    }
  }, [backendUrl]);

  const filterWaves = useCallback(() => {
    let filtered = waves;

    if (searchTerm) {
      filtered = filtered.filter(wave =>
        wave.wave_number.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(wave => wave.status === statusFilter);
    }

    setFilteredWaves(filtered);
  }, [waves, searchTerm, statusFilter]);

  useEffect(() => {
    fetchWaves();
  }, [fetchWaves]);

  useEffect(() => {
    filterWaves();
  }, [filterWaves]);

  const handleAutoCreateWaves = async () => {
    if (!window.confirm('Auto-create picking waves from pending deliveries?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/wave-picking/waves/auto-create`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Created ${data.length} picking wave(s)`);
        fetchWaves();
      } else {
        toast.error('Failed to auto-create waves');
      }
    } catch (error) {
      toast.error('Error auto-creating waves');
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseWave = async (waveNumber) => {
    if (!window.confirm(`Release wave ${waveNumber} for picking?`)) return;

    try {
      const response = await fetch(`${backendUrl}/api/wave-picking/waves/${waveNumber}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ released_by: 'USER' })
      });

      if (response.ok) {
        toast.success('Wave released successfully');
        fetchWaves();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to release wave');
      }
    } catch (error) {
      toast.error('Error releasing wave');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Created': 'bg-orange-100 text-orange-800',
      'Released': 'bg-green-100 text-green-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-gray-100 text-gray-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return <Badge className={colors[priority] || 'bg-gray-100 text-gray-800'}>{priority}</Badge>;
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Wave Picking</h1>
            <p className="text-gray-600">Automated warehouse picking wave management</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchWaves}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleAutoCreateWaves} disabled={loading}>
              <Waves className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Auto-Create Waves'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by wave number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Created">Created</SelectItem>
                <SelectItem value="Released">Released</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Waves Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wave Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead className="text-right">Total Items</TableHead>
                <TableHead className="text-right">Total Quantity</TableHead>
                <TableHead className="text-right">Picked</TableHead>
                <TableHead>Planned Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWaves.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    No picking waves found. Click "Auto-Create Waves" to generate from pending deliveries.
                  </TableCell>
                </TableRow>
              ) : (
                filteredWaves.map((wave) => (
                  <TableRow key={wave.wave_number}>
                    <TableCell className="font-medium">{wave.wave_number}</TableCell>
                    <TableCell>{wave.wave_type}</TableCell>
                    <TableCell>{getPriorityBadge(wave.priority)}</TableCell>
                    <TableCell className="text-right">{wave.total_items}</TableCell>
                    <TableCell className="text-right">{wave.total_quantity.toFixed(0)}</TableCell>
                    <TableCell className="text-right">{wave.picked_quantity.toFixed(0)}</TableCell>
                    <TableCell>{wave.planned_pick_date || '-'}</TableCell>
                    <TableCell>{getStatusBadge(wave.status)}</TableCell>
                    <TableCell>
                      {wave.status === 'Created' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReleaseWave(wave.wave_number)}
                        >
                          Release
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredWaves.length} of {waves.length} waves
        </div>
      </div>
    </Layout>
  );
};

export default WavePickingPage;